import { pickT } from "@repo/shared";
import type {
  CustomFieldOption,
  FinancialSituationModelSettings,
  FlowField,
  FlowRequirementIssue,
} from "@repo/shared/types";
import {
  AlertTriangle,
  ArrowLeft,
  ChevronDown,
  Copy,
  GripVertical,
  Settings,
  Trash2,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  getFlowComponentLabel,
  getFlowComponentRowLabel,
} from "@/components/flow/flow-component-types";
import { PageHeader } from "@/components/page-header";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Link } from "@/components/ui/link";
import { Skeleton } from "@/components/ui/skeleton";
import { useConfirm } from "@/hooks/use-confirm";
import { ApiError, del, patch, post, put } from "@/lib/api/_client";
import { checkComponentDelete } from "@/lib/api/flow-editor";
import { useDeleteCheck } from "@/lib/hooks/use-delete-check";
import { useFlowPage } from "@/lib/hooks/use-flows";
import { usePortfolioCollectionSettings } from "@/lib/hooks/use-portfolio-collections";
import { useSumsubMode } from "@/lib/hooks/use-sumsub";
import { useTrapetsMode } from "@/lib/hooks/use-trapets";
import type { FlowPageComponent, FlowPageLayout } from "@/lib/types/flow";
import { getComponentIcon } from "../../../../shared/component-icons";
import { AddComponentDialog } from "../../add-component-dialog";
import { ColumnSettingsDialog } from "../../column-settings-dialog";
import { ComponentSettingsDialog } from "../../component-settings-dialog";
import { FlowPageDialog } from "../../flow-page-dialog";
import { LayoutSettingsDialog } from "../../layout-settings-dialog";
import { RequirementIssuesAlert } from "../../requirement-issues-alert";
import { TabSettingsDialog } from "../../tab-settings-dialog";
import { LayoutBlock } from "./layout-block";
import { T } from "./page-editor.i18n";
import { TabsBlock } from "./tabs-block";

type PageWithComponents = {
  id: string;
  name: string;
  visibility: string;
  pageType?: string;
  conditionalVisibility?: any;
  components: (FlowPageComponent & { flowField?: FlowField })[];
  /** PR 546 — unmet flow requirements of this page's components, computed server-side. */
  requirementIssues?: FlowRequirementIssue[];
  layouts?: (FlowPageLayout & {
    layoutType?: string;
    components: (FlowPageComponent & { flowField?: FlowField })[];
  })[];
  flow?: {
    name: string;
    language: string;
    fields: FlowField[];
    riskModelId?: string | null;
    portfolioCollectionId?: string | null;
    /** QL-401 — whether advisors can start followups with this flow. */
    supportedForFollowup?: boolean;
    financialSituationModel?: {
      id: string;
      incomeFields: string;
      assetFields: string;
      liabilityFields: string;
      commitmentFields: string;
      currency: string;
      settings?: FinancialSituationModelSettings | null;
    } | null;
  };
};

type ClientCustomField = {
  id: string;
  name: string;
  type: string;
  options?: CustomFieldOption[] | null;
  isActive: boolean;
};

type PageEditorProps = {
  flowId: string;
  pageId: string;
  clientCustomFields?: ClientCustomField[];
  hasXRayAccess?: boolean;
};

export function PageEditor({
  flowId,
  pageId,
  clientCustomFields = [],
  hasXRayAccess = false,
}: PageEditorProps) {
  const { page: pageData, isLoading, mutate } = useFlowPage(flowId, pageId);

  const page = pageData as PageWithComponents | null;
  const flowName = page?.flow?.name || "";
  const flowLanguage = page?.flow?.language || "en_GB";
  const availableFields = page?.flow?.fields || [];
  const hasRiskModel = !!page?.flow?.riskModelId;
  const hasPortfolioCollection = !!page?.flow?.portfolioCollectionId;
  const { settings: collectionSettings } = usePortfolioCollectionSettings(
    page?.flow?.portfolioCollectionId ?? null,
  );
  const { trapetsMode } = useTrapetsMode();
  const { sumsubMode } = useSumsubMode();
  const financialSituationModel = page?.flow?.financialSituationModel || null;

  // Use flow language for all UI in the flow editor
  const t = pickT(T, flowLanguage);
  const [components, setComponents] = useState<(FlowPageComponent & { flowField?: FlowField })[]>(
    page?.components || [],
  );
  const [draggedItem, setDraggedItem] = useState<{
    id: string;
    type: "page-item" | "layout-component" | "tabs-component";
    sourceLayoutId?: string;
    sourceColumn?: "left" | "right";
    sourceTabId?: string;
    itemType?: "layout" | "component";
  } | null>(null);
  const [pageDialogOpen, setPageDialogOpen] = useState(false);
  const [addComponentDialogOpen, setAddComponentDialogOpen] = useState(false);
  const [componentSettingsDialogOpen, setComponentSettingsDialogOpen] = useState(false);
  const [layoutSettingsDialogOpen, setLayoutSettingsDialogOpen] = useState(false);
  const [editingComponent, setEditingComponent] = useState<
    (FlowPageComponent & { flowField?: FlowField }) | null
  >(null);
  const [editingLayout, setEditingLayout] = useState<FlowPageLayout | null>(null);
  const [columnSettingsDialogOpen, setColumnSettingsDialogOpen] = useState(false);
  const [editingColumn, setEditingColumn] = useState<"left" | "right">("left");
  const [tabSettingsDialogOpen, setTabSettingsDialogOpen] = useState(false);
  const [editingTabId, setEditingTabId] = useState<string | null>(null);
  const [deletingComponent, setDeletingComponent] = useState<
    (FlowPageComponent & { flowField?: FlowField }) | null
  >(null);
  const [loading, setLoading] = useState(false);
  const [targetLayout, setTargetLayout] = useState<{
    layoutId: string;
    column?: "left" | "right";
    tabId?: string;
  } | null>(null);
  const { confirm, ConfirmationDialog } = useConfirm();

  // QL-168 — a component the server would refuse to delete shows why straight
  // away; the confirmation only opens when the delete is allowed. The clicked
  // component is kept here because layout/tab components are not in
  // `components`, so the id alone cannot be looked up.
  const pendingDeleteRef = useRef<(FlowPageComponent & { flowField?: FlowField }) | null>(null);
  const { requestDelete, checkingId } = useDeleteCheck({
    check: (componentId) => checkComponentDelete(flowId, componentId),
    onAllowed: (componentId) => {
      if (pendingDeleteRef.current?.id === componentId) {
        setDeletingComponent(pendingDeleteRef.current);
      }
    },
    onBlocked: (reason) =>
      confirm({
        title: t.error,
        description: reason || t.failedToDeleteComponent,
        confirmText: t.ok,
        variant: "destructive",
        type: "alert",
      }),
  });

  // Sync components state with page.components when it changes
  useEffect(() => {
    if (page?.components) {
      setComponents(page.components);
    }
  }, [page?.components]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!page) {
    return <div className="text-muted-foreground">{t.pageNotFound}</div>;
  }

  const handleDeleteComponent = (component: FlowPageComponent & { flowField?: FlowField }) => {
    pendingDeleteRef.current = component;
    requestDelete(component.id);
  };

  const confirmDeleteComponent = async () => {
    if (!deletingComponent) return;

    setLoading(true);
    try {
      await del(`/internal/flows/${flowId}/components/${deletingComponent.id}`);
      setComponents(components.filter((c) => c.id !== deletingComponent.id));
      setDeletingComponent(null);
      mutate();
    } catch (err) {
      const message = err instanceof ApiError ? err.message : t.failedToDeleteComponent;
      await confirm({
        title: t.error,
        description: message,
        confirmText: t.ok,
        variant: "destructive",
        type: "alert",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicateComponent = async (
    component: FlowPageComponent & { flowField?: FlowField },
  ) => {
    setLoading(true);
    try {
      await post(`/internal/flows/${flowId}/components/${component.id}/duplicate`, {});
      mutate();
    } catch (err) {
      const message = err instanceof ApiError ? err.message : t.failedToDuplicateComponent;
      await confirm({
        title: t.error,
        description: message,
        confirmText: t.ok,
        variant: "destructive",
        type: "alert",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleComponentSettings = (component: FlowPageComponent & { flowField?: FlowField }) => {
    setEditingComponent(component);
    setComponentSettingsDialogOpen(true);
  };

  const handleLayoutSettings = (layout: FlowPageLayout) => {
    setEditingLayout(layout);
    setLayoutSettingsDialogOpen(true);
  };

  const handleColumnSettings = (layout: FlowPageLayout, column: "left" | "right") => {
    setEditingLayout(layout);
    setEditingColumn(column);
    setColumnSettingsDialogOpen(true);
  };

  const handleAddLayout = async (layoutType: "two_column" | "tabs_block" = "two_column") => {
    setLoading(true);

    // Get max order among all page items (layouts + standalone components)
    const maxLayoutOrder = Math.max(
      ...(page.layouts || []).map((l: any) => l.order),
      ...components.filter((c) => !c.flowPageLayoutId).map((c) => c.order),
      -1,
    );

    try {
      await post(`/internal/flows/${flowId}/pages/${page.id}/layouts`, {
        order: maxLayoutOrder + 1,
        layoutType,
      });
      mutate();
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : `${t.failedToCreateBlock} ${layoutType === "tabs_block" ? t.tabsBlock : t.twoColumnBlock} ${t.block}`;
      await confirm({
        title: t.error,
        description: message,
        confirmText: t.ok,
        variant: "destructive",
        type: "alert",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLayout = async (layoutId: string) => {
    // Find the layout to determine its type
    const layout = (page.layouts as any[])?.find((l: any) => l.id === layoutId);
    const isTabsBlock = layout?.layoutType === "tabs_block";

    const confirmed = await confirm({
      title: isTabsBlock ? t.deleteTabsBlock : t.delete2ColumnBlock,
      description: isTabsBlock ? t.tabsComponentsMoved : t.columnComponentsFullWidth,
      confirmText: t.delete,
      cancelText: t.cancel,
      variant: "destructive",
    });

    if (!confirmed) return;

    setLoading(true);
    try {
      await del(`/internal/flows/${flowId}/layouts/${layoutId}`);
      mutate();
    } catch (err) {
      const message = err instanceof ApiError ? err.message : t.failedToDeleteLayout;
      await confirm({
        title: t.error,
        description: message,
        confirmText: t.ok,
        variant: "destructive",
        type: "alert",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePageDialogClose = (success: boolean) => {
    setPageDialogOpen(false);
    if (success) {
      mutate();
    }
  };

  const handleAddComponentDialogClose = (success: boolean) => {
    setAddComponentDialogOpen(false);
    setTargetLayout(null);
    if (success) {
      mutate();
    }
  };

  const handleAddToColumn = (layoutId: string, column: "left" | "right") => {
    setTargetLayout({ layoutId, column });
    setAddComponentDialogOpen(true);
  };

  // Tab-specific handlers
  const handleAddToTab = (layoutId: string, tabId: string) => {
    setTargetLayout({ layoutId, tabId });
    setAddComponentDialogOpen(true);
  };

  const handleTabSettings = (layout: FlowPageLayout, tabId: string) => {
    setEditingLayout(layout);
    setEditingTabId(tabId);
    setTabSettingsDialogOpen(true);
  };

  const handleAddTab = async (layoutId: string) => {
    try {
      await post(`/internal/flows/${flowId}/layouts/${layoutId}/tabs`);
      mutate();
    } catch (err) {
      const message = err instanceof ApiError ? err.message : t.failedToAddTab;
      await confirm({
        title: t.error,
        description: message,
        confirmText: t.ok,
        variant: "destructive",
        type: "alert",
      });
    }
  };

  const handleDeleteTab = async (layoutId: string, tabId: string) => {
    const confirmed = await confirm({
      title: t.deleteTab,
      description: t.deleteTabDescription,
      confirmText: t.deleteTab,
      cancelText: t.cancel,
    });

    if (!confirmed) return;

    try {
      await del(`/internal/flows/${flowId}/layouts/${layoutId}/tabs/${tabId}`);
      mutate();
    } catch (err) {
      const message = err instanceof ApiError ? err.message : t.failedToDeleteTab;
      await confirm({
        title: t.error,
        description: message,
        confirmText: t.ok,
        variant: "destructive",
        type: "alert",
      });
    }
  };

  const handleRenameTab = async (layoutId: string, tabId: string, newName: string) => {
    try {
      await patch(`/internal/flows/${flowId}/layouts/${layoutId}/tabs/${tabId}`, { name: newName });
      mutate();
    } catch (err) {
      const message = err instanceof ApiError ? err.message : t.failedToRenameTab;
      await confirm({
        title: t.error,
        description: message,
        confirmText: t.ok,
        variant: "destructive",
        type: "alert",
      });
    }
  };

  const handleReorderTab = (
    layoutId: string,
    tabId: string,
    componentOrders: { id: string; order: number }[],
  ) => {
    put(`/internal/flows/${flowId}/layouts/${layoutId}/tabs/${tabId}/reorder`, { componentOrders })
      .then(() => {
        mutate();
      })
      .catch((err) => {
        const message = err instanceof ApiError ? err.message : t.failedToReorderComponents;
        confirm({
          title: t.error,
          description: message,
          confirmText: t.ok,
          variant: "destructive",
          type: "alert",
        });
        mutate();
      });
  };

  const handleMoveComponentToTab = async (
    componentId: string,
    layoutId: string,
    tabId: string,
    order: number,
  ) => {
    try {
      await put(`/internal/flows/${flowId}/components/${componentId}/move-to-tab`, {
        layoutId,
        tabId,
        order,
      });
      setTimeout(() => mutate(), 100);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : t.failedToMoveComponent;
      await confirm({
        title: t.error,
        description: message,
        confirmText: t.ok,
        variant: "destructive",
        type: "alert",
      });
      mutate();
    }

    setDraggedItem(null);
  };

  // SIMPLIFIED: Single handler for reordering components within a column
  const handleReorderColumn = (
    layoutId: string,
    column: "left" | "right",
    componentOrders: { id: string; order: number }[],
  ) => {
    put(`/internal/flows/${flowId}/layouts/${layoutId}/columns/${column}/reorder`, {
      componentOrders,
    })
      .then(() => {
        mutate();
      })
      .catch((err) => {
        const message = err instanceof ApiError ? err.message : t.failedToReorderComponents;
        confirm({
          title: t.error,
          description: message,
          confirmText: t.ok,
          variant: "destructive",
          type: "alert",
        });
        mutate();
      });
  };

  // SIMPLIFIED: Single handler for moving components to layouts
  const handleMoveComponentToLayout = async (
    componentId: string,
    layoutId: string,
    column: "left" | "right",
    order: number,
  ) => {
    try {
      await put(`/internal/flows/${flowId}/components/${componentId}/move-to-layout`, {
        layoutId,
        column,
        order,
      });
      setTimeout(() => mutate(), 100);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : t.failedToMoveComponent;
      await confirm({
        title: t.error,
        description: message,
        confirmText: t.ok,
        variant: "destructive",
        type: "alert",
      });
      mutate();
    }

    setDraggedItem(null);
  };

  // SIMPLIFIED: Single handler for moving components to standalone
  const handleMoveToStandalone = async (componentId: string, targetIndex: number) => {
    try {
      await put(`/internal/flows/${flowId}/components/${componentId}/move-to-standalone`, {
        targetIndex,
      });
      setTimeout(() => mutate(), 100);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : t.failedToMoveComponent;
      await confirm({
        title: t.error,
        description: message,
        confirmText: t.ok,
        variant: "destructive",
        type: "alert",
      });
      mutate();
    }

    setDraggedItem(null);
  };

  // SIMPLIFIED: Handler for reordering page-level items (layouts and standalone components)
  const handleReorderPageItems = async (draggedId: string, targetIndex: number) => {
    setLoading(true);

    // Build current page items list
    const layouts = page.layouts || [];
    const standaloneComponents = components.filter((c) => !c.flowPageLayoutId);
    const allItems: Array<{
      id: string;
      type: "layout" | "component";
      order: number;
    }> = [
      ...layouts.map((l) => ({
        id: l.id,
        type: "layout" as const,
        order: l.order,
      })),
      ...standaloneComponents.map((c) => ({
        id: c.id,
        type: "component" as const,
        order: c.order,
      })),
    ].sort((a, b) => a.order - b.order);

    // Find current position
    const currentIndex = allItems.findIndex((item) => item.id === draggedId);
    if (currentIndex === -1 || currentIndex === targetIndex) {
      setLoading(false);
      setDraggedItem(null);
      return;
    }

    // Reorder
    const reorderedItems = [...allItems];
    const [movedItem] = reorderedItems.splice(currentIndex, 1);
    reorderedItems.splice(targetIndex, 0, movedItem);

    // Build update payload
    const pageItems = reorderedItems.map((item, index) => ({
      id: item.id,
      type: item.type,
      order: index,
    }));

    try {
      await put(`/internal/flows/${flowId}/pages/${page.id}/items/reorder`, { pageItems });
      mutate();
    } catch (err) {
      const message = err instanceof ApiError ? err.message : t.failedToReorderItems;
      await confirm({
        title: t.error,
        description: message,
        confirmText: t.ok,
        variant: "destructive",
        type: "alert",
      });
    }

    setDraggedItem(null);
    setLoading(false);
  };

  const handleComponentSettingsClose = (success: boolean) => {
    setComponentSettingsDialogOpen(false);
    setEditingComponent(null);
    if (success) {
      mutate();
    }
  };

  const componentsWithIssues = new Set(
    (page?.requirementIssues ?? []).flatMap((issue) => issue.components.map((c) => c.componentId)),
  );

  const getComponentLabel = (component: FlowPageComponent & { flowField?: FlowField }) =>
    getFlowComponentRowLabel(component, flowLanguage);

  const hasConditionalDisplay = (component: FlowPageComponent) => {
    try {
      const config = component.config ? JSON.parse(component.config) : null;
      return config?.conditionalDisplay?.enabled === true;
    } catch {
      return false;
    }
  };

  return (
    <div className="space-y-6">
      {/* QL-206 — no flow tab bar here: the flow's Settings/Fields/Pages tabs read as
          something you could add to this page. The breadcrumb and back arrow lead
          back to the flow. */}
      <PageHeader
        breadcrumbs={[
          { label: t.settings, href: "/settings" },
          { label: t.flows, href: "/settings/flows" },
          {
            label: flowName,
            href: `/settings/flows/${flowId}?tab=pages&visibility=${page.visibility}`,
          },
          { label: page.name },
        ]}
        title={page.name}
        leading={
          <Link to={`/settings/flows/${flowId}?tab=pages&visibility=${page.visibility}`}>
            <Button variant="ghost" size="icon" tooltip={t.backToPages}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
        }
        titleAddon={
          <>
            <Badge variant="secondary">
              {page.visibility === "internal" ? t.internal : t.public}
            </Badge>
            <Badge variant="secondary">
              {page.pageType === "goal"
                ? t.goalPage
                : page.pageType === "portfolio"
                  ? t.portfolioPage
                  : t.globalPage}
            </Badge>
            {page.conditionalVisibility && (
              <Badge variant="secondary" className="text-xs">
                {t.conditionalVisibility}
              </Badge>
            )}
          </>
        }
        actions={
          <>
            <Button variant="outline" size="sm" onClick={() => setPageDialogOpen(true)}>
              {t.pageSettings}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" disabled={loading}>
                  {t.add}
                  <ChevronDown className="h-4 w-4 ml-1 opacity-70" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => setAddComponentDialogOpen(true)}>
                  {t.component}
                </DropdownMenuItem>
                {page.visibility !== "public" && (
                  <>
                    <DropdownMenuItem onSelect={() => handleAddLayout("tabs_block")}>
                      {t.tabBlock}
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => handleAddLayout("two_column")}>
                      {t.twoColumnBlockLabel}
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        }
      />

      <RequirementIssuesAlert
        issues={page.requirementIssues}
        language={flowLanguage}
        showPages={false}
      />

      {/* Layouts & Components - SIMPLIFIED DRAG SYSTEM */}
      <div className="space-y-4">
        {(() => {
          // Combine layouts and standalone components into ordered array
          const layouts = page.layouts || [];
          const standaloneComponents = components.filter((c) => !c.flowPageLayoutId);

          const pageItems: Array<{
            type: "layout" | "component";
            id: string;
            order: number;
            data?: any;
          }> = [
            ...layouts.map((l) => ({
              type: "layout" as const,
              id: l.id,
              order: l.order,
              data: l,
            })),
            ...standaloneComponents.map((c) => ({
              type: "component" as const,
              id: c.id,
              order: c.order,
              data: c,
            })),
          ].sort((a, b) => a.order - b.order);

          // Show empty state if no items
          if (pageItems.length === 0) {
            return (
              <Card className="p-12 text-center text-muted-foreground">
                <p>{t.noComponentsYet}</p>
                {page.visibility !== "public" && (
                  <p className="text-sm mt-2">{t.addComponentsHint}</p>
                )}
              </Card>
            );
          }

          return (
            <>
              {pageItems.map((item, index) => {
                const isDragging = draggedItem?.id === item.id && draggedItem.type === "page-item";

                return (
                  <div key={item.id}>
                    {/* The actual item */}
                    {item.type === "layout" ? (
                      item.data.layoutType === "tabs_block" ? (
                        <div
                          data-tabs-block-container
                          className={`${isDragging ? "opacity-50" : ""} transition-all`}
                        >
                          <TabsBlock
                            layoutId={item.data.id}
                            layoutConfig={item.data.config}
                            components={item.data.components}
                            flowLanguage={flowLanguage}
                            onDelete={() => handleDeleteLayout(item.data.id)}
                            onLayoutSettings={() => handleLayoutSettings(item.data)}
                            onTabSettings={(tabId) => handleTabSettings(item.data, tabId)}
                            onAddToTab={(tabId) => handleAddToTab(item.data.id, tabId)}
                            onAddTab={() => handleAddTab(item.data.id)}
                            onDeleteTab={(tabId) => handleDeleteTab(item.data.id, tabId)}
                            onRenameTab={(tabId, newName) =>
                              handleRenameTab(item.data.id, tabId, newName)
                            }
                            onComponentSettings={handleComponentSettings}
                            onComponentDelete={handleDeleteComponent}
                            onComponentDuplicate={handleDuplicateComponent}
                            onReorderTab={(tabId, orders) =>
                              handleReorderTab(item.data.id, tabId, orders)
                            }
                            onMoveComponentBetweenTabs={(componentId, toTabId, toOrder) =>
                              handleMoveComponentToTab(componentId, item.data.id, toTabId, toOrder)
                            }
                            onDragStart={(componentId, tabId) => {
                              setDraggedItem({
                                id: componentId,
                                type: "tabs-component",
                                sourceLayoutId: item.data.id,
                                sourceTabId: tabId,
                              });
                            }}
                            onDragEnd={() => {
                              setDraggedItem(null);
                            }}
                            onLayoutDragStart={() => {
                              setDraggedItem({
                                id: item.data.id,
                                type: "page-item",
                                itemType: "layout",
                              });
                            }}
                            onLayoutDrop={() => {
                              if (
                                draggedItem?.type === "page-item" &&
                                draggedItem.id !== item.data.id
                              ) {
                                handleReorderPageItems(draggedItem.id, index);
                              } else if (
                                draggedItem?.type === "tabs-component" ||
                                draggedItem?.type === "layout-component"
                              ) {
                                handleMoveToStandalone(draggedItem.id, index);
                              }
                            }}
                            draggedItem={draggedItem}
                            disabled={loading}
                          />
                        </div>
                      ) : (
                        <div
                          data-layout-block-container
                          className={`${isDragging ? "opacity-50" : ""} transition-all`}
                        >
                          <LayoutBlock
                            layoutId={item.data.id}
                            layoutConfig={item.data.config}
                            leftComponents={item.data.components.filter(
                              (c: any) => c.columnPosition === "left",
                            )}
                            rightComponents={item.data.components.filter(
                              (c: any) => c.columnPosition === "right",
                            )}
                            flowLanguage={flowLanguage}
                            onDelete={() => handleDeleteLayout(item.data.id)}
                            onLayoutSettings={() => handleLayoutSettings(item.data)}
                            onColumnSettings={(column) => handleColumnSettings(item.data, column)}
                            onAddToLeft={() => handleAddToColumn(item.data.id, "left")}
                            onAddToRight={() => handleAddToColumn(item.data.id, "right")}
                            onComponentSettings={handleComponentSettings}
                            onComponentDelete={handleDeleteComponent}
                            onComponentDuplicate={handleDuplicateComponent}
                            onReorderColumn={(column, orders) =>
                              handleReorderColumn(item.data.id, column, orders)
                            }
                            onMoveComponentBetweenColumns={(componentId, toColumn, toOrder) =>
                              handleMoveComponentToLayout(
                                componentId,
                                item.data.id,
                                toColumn,
                                toOrder,
                              )
                            }
                            onDragStart={(componentId, column) => {
                              setDraggedItem({
                                id: componentId,
                                type: "layout-component",
                                sourceLayoutId: item.data.id,
                                sourceColumn: column,
                              });
                            }}
                            onDragEnd={() => {
                              setDraggedItem(null);
                            }}
                            onLayoutDragStart={() => {
                              setDraggedItem({
                                id: item.data.id,
                                type: "page-item",
                                itemType: "layout",
                              });
                            }}
                            onLayoutDrop={() => {
                              if (
                                draggedItem?.type === "page-item" &&
                                draggedItem.id !== item.data.id
                              ) {
                                handleReorderPageItems(draggedItem.id, index);
                              } else if (draggedItem?.type === "layout-component") {
                                handleMoveToStandalone(draggedItem.id, index);
                              }
                            }}
                            draggedItem={draggedItem}
                            disabled={loading}
                          />
                        </div>
                      )
                    ) : (
                      // biome-ignore lint/a11y/noStaticElementInteractions: drag handle requires drag events
                      <div
                        draggable={!loading}
                        onDragStart={(e) => {
                          // Get the actual width of the element being dragged
                          const elementWidth = (e.currentTarget as HTMLElement).offsetWidth;

                          // Create custom drag preview - full-width box matching actual element
                          const dragPreview = document.createElement("div");
                          dragPreview.style.cssText = `
                            position: fixed;
                            top: -9999px;
                            left: -9999px;
                            width: ${elementWidth}px;
                            height: 60px;
                            background: rgba(59, 130, 246, 0.2);
                            border: 2px dashed rgb(59, 130, 246);
                            border-radius: 8px;
                            padding: 16px;
                            font-size: 14px;
                            font-weight: 500;
                            color: rgb(59, 130, 246);
                            display: flex;
                            align-items: center;
                            justify-content: center;
                          `;
                          dragPreview.textContent = t.movingComponent;
                          document.body.appendChild(dragPreview);
                          e.dataTransfer.setDragImage(dragPreview, 20, 30);

                          // Clean up after next frame
                          requestAnimationFrame(() => {
                            document.body.removeChild(dragPreview);
                          });

                          setDraggedItem({
                            id: item.data.id,
                            type: "page-item",
                            itemType: "component",
                          });
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.dataTransfer.dropEffect = "move";
                          e.currentTarget.classList.add("ring-2", "ring-primary");
                        }}
                        onDragLeave={(e) => {
                          e.currentTarget.classList.remove("ring-2", "ring-primary");
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.currentTarget.classList.remove("ring-2", "ring-primary");

                          if (draggedItem?.type === "layout-component") {
                            // Move component out of layout to standalone - insert BEFORE this position
                            handleMoveToStandalone(draggedItem.id, index);
                          } else if (
                            draggedItem?.type === "page-item" &&
                            draggedItem.id !== item.data.id
                          ) {
                            // Reorder page items - insert at this position (before)
                            handleReorderPageItems(draggedItem.id, index);
                          }
                        }}
                        onDragEnd={() => setDraggedItem(null)}
                        className={`${isDragging || draggedItem?.id === item.data.id ? "opacity-50" : ""} transition-all`}
                      >
                        <div className="flex items-center gap-3 p-3 border rounded-lg bg-background hover:bg-accent/50 transition-colors cursor-move">
                          <GripVertical className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                          {(() => {
                            const ComponentIcon = getComponentIcon(item.data.componentType);
                            return (
                              <ComponentIcon className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                            );
                          })()}

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-xs flex-shrink-0">
                                {getFlowComponentLabel(item.data.componentType, flowLanguage)}
                              </Badge>
                              <span className="font-medium truncate">
                                {getComponentLabel(item.data)}
                              </span>
                              {item.data.componentType === "field" &&
                                item.data.flowField?.isRequired && (
                                  <Badge variant="destructive" className="text-xs">
                                    {t.required}
                                  </Badge>
                                )}
                              {hasConditionalDisplay(item.data) && (
                                <Badge variant="secondary" className="text-xs">
                                  {t.conditional}
                                </Badge>
                              )}
                              {componentsWithIssues.has(item.data.id) && (
                                <Badge variant="secondary" className="text-xs gap-1">
                                  <AlertTriangle className="h-3 w-3 text-warning" />
                                  {t.needsSetup}
                                </Badge>
                              )}
                            </div>
                          </div>

                          <div className="flex gap-1 flex-shrink-0">
                            <Button
                              variant="ghost"
                              size="icon"
                              tooltip={t.settings}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleComponentSettings(item.data);
                              }}
                            >
                              <Settings className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              tooltip={t.duplicateComponent}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDuplicateComponent(item.data);
                              }}
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              tooltip={t.delete}
                              loading={checkingId === item.data.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteComponent(item.data);
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          );
        })()}
      </div>

      {/* Dialogs */}
      <FlowPageDialog
        flowId={flowId}
        flowLanguage={flowLanguage}
        page={page as any}
        availableFields={availableFields}
        clientCustomFields={clientCustomFields}
        portfolioCollectionId={page.flow?.portfolioCollectionId ?? null}
        open={pageDialogOpen}
        onClose={handlePageDialogClose}
      />

      <AddComponentDialog
        open={addComponentDialogOpen}
        pageId={page.id}
        flowId={flowId}
        flowLanguage={flowLanguage}
        pageType={(page.pageType as "global" | "goal" | "portfolio") || "global"}
        portfolioCollectionId={page.flow?.portfolioCollectionId ?? null}
        availableFields={availableFields}
        clientCustomFields={clientCustomFields}
        targetLayout={targetLayout}
        onClose={handleAddComponentDialogClose}
        hasXRayAccess={hasXRayAccess}
        hasRiskModel={hasRiskModel}
        riskModelId={page.flow?.riskModelId ?? null}
        hasPortfolioCollection={hasPortfolioCollection}
        financialSituationModel={financialSituationModel}
        componentVisibility={page.visibility as "public" | "internal"}
        collectionSettings={collectionSettings}
        trapetsMode={trapetsMode}
        sumsubMode={sumsubMode}
      />

      <ComponentSettingsDialog
        open={componentSettingsDialogOpen}
        component={editingComponent}
        flowId={flowId}
        flowLanguage={flowLanguage}
        availableFields={availableFields}
        clientCustomFields={clientCustomFields}
        onClose={handleComponentSettingsClose}
        hasXRayAccess={hasXRayAccess}
        pageType={(page.pageType as "global" | "goal" | "portfolio") || "global"}
        portfolioCollectionId={page.flow?.portfolioCollectionId ?? null}
        pageVisibility={page.visibility as "public" | "internal"}
        financialSituationModel={financialSituationModel}
        flowSupportsFollowup={page.flow?.supportedForFollowup === true}
      />

      <LayoutSettingsDialog
        open={layoutSettingsDialogOpen}
        flowId={flowId}
        layout={editingLayout}
        flowLanguage={flowLanguage}
        availableFields={availableFields}
        clientCustomFields={clientCustomFields}
        onClose={(success) => {
          setLayoutSettingsDialogOpen(false);
          setEditingLayout(null);
          if (success) {
            mutate();
          }
        }}
        pageType={(page.pageType as "global" | "goal" | "portfolio") || "global"}
        portfolioCollectionId={page.flow?.portfolioCollectionId ?? null}
      />

      <ColumnSettingsDialog
        open={columnSettingsDialogOpen}
        flowId={flowId}
        layout={editingLayout}
        column={editingColumn}
        flowLanguage={flowLanguage}
        onClose={(success) => {
          setColumnSettingsDialogOpen(false);
          setEditingLayout(null);
          if (success) {
            mutate();
          }
        }}
      />

      <TabSettingsDialog
        open={tabSettingsDialogOpen}
        flowId={flowId}
        layout={editingLayout}
        tabId={editingTabId}
        flowLanguage={flowLanguage}
        availableFields={availableFields}
        clientCustomFields={clientCustomFields}
        onClose={(success) => {
          setTabSettingsDialogOpen(false);
          setEditingLayout(null);
          setEditingTabId(null);
          if (success) {
            mutate();
          }
        }}
        pageType={(page.pageType as "global" | "goal" | "portfolio") || "global"}
        portfolioCollectionId={page.flow?.portfolioCollectionId ?? null}
      />

      <AlertDialog
        open={!!deletingComponent}
        onOpenChange={(open) => !open && setDeletingComponent(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t.deleteComponent}</AlertDialogTitle>
            <AlertDialogDescription>{t.deleteComponentDescription}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={loading}>{t.cancel}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteComponent}
              disabled={loading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {loading ? t.deleting : t.delete}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <ConfirmationDialog />
    </div>
  );
}
